<template>
  <b-container v-if="purchaseFlowStatus != allStatus[0]" class="purchase-flow-container">
    <div class="my-4">
      <b-row class="p-3">
        <b-col>
          <Header :status="purchaseFlowStatus" />
        </b-col>
      </b-row>
      <b-row class="p-3">
        <b-col cols="12" class="mb-4 mb-lg-0 mr-lg-2">
          <Login
            v-if="purchaseFlowStatus == allStatus[1]"
            :force-toggle="true"
            :after-login="afterLoginPath"
            @login="login"
            @signup="login"
          />
          <Payments v-if="purchaseFlowStatus == allStatus[2]" @payment="payment" />
          <PaymentSuccessful
            v-if="purchaseFlowStatus == allStatus[3]"
            :page="false"
            :vector="actualVector"
          />
        </b-col>
      </b-row>
    </div>
  </b-container>
  <div v-else class="background" :class="{ 'pt-4': showTopPadding }">
    <b-container class="purchase-flow-container" :class="{ 'p-4': !alternativeLayout }">
      <Plans
        :custom-plans="customPlans"
        :individual-vector-plans="isPreviewPage"
        :see-more-link="seeMoreLink"
        :lifetime="lifetime"
        :cant-download-reason="cantDownloadReason"
        :prismic-props="prismicProps"
        :u-id="uId"
        :annual-discount="annualDiscount"
        :alternative-layout="alternativeLayout"
        @suscribe-click="plansSubscribeClick"
      />
      <div v-if="isPreviewPage" class="text-center mt-4">
        <nuxt-link
          v-if="!isFree || cantDownloadReason"
          class="see-more"
          :to="{ path: seeMoreLink, hash: 'compare-plans' }"
          @click.native="$bvModal.hide('preview-vector-modal')"
        >
          {{ $t('main.purchaseFlow.purchaseFlow.text') }}
        </nuxt-link>
        <a v-else class="see-more cursor-pointer" @click="freeDownload()">
          {{ $t('preview.download.main.freeDownload') }}
        </a>
      </div>
    </b-container>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Header from '@/components/PurchaseFlow/Header';
import Payments from '@/components/PurchaseFlow/Payments';
import Plans from '@/components/PurchaseFlow/Plans';
import Login from '@/components/User/Form';
import PaymentSuccessful from '@/components/PurchaseFlow/PaymentSuccessful';

export default {
  components: {
    Header,
    Login,
    Payments,
    Plans,
    PaymentSuccessful
  },
  props: {
    showPlans: {
      type: Boolean,
      default: false,
      required: false
    },
    plansNewPage: {
      type: Boolean,
      default: false,
      required: false
    },
    showTopPadding: {
      type: Boolean,
      default: true
    },
    plansPage: {
      type: Boolean,
      default: false,
      required: false
    },
    lifetime: {
      type: Boolean,
      default: false,
      required: false
    },
    prismicProps: {
      type: Object,
      default: () => ({})
    },
    customPlans: {
      type: Array,
      default: () => [],
      required: false
    },
    annualDiscount: {
      type: Number,
      default: 25,
      required: false
    }
  },
  data() {
    return {
      allStatus: ['plans', 'account', 'payment', 'download'],
      cantDownloadReason: '',
      uId: this._uid
    };
  },
  computed: {
    ...mapGetters('plans', ['openPlan']),
    ...mapState('auth', ['user']),
    ...mapGetters('vectors', ['actualVector', 'purchaseFlowStatus']),
    ...mapGetters('downloads', ['canDownload', 'pathDownload', 'elementReason']),
    isFree() {
      return this.canDownload(this.actualVector.id);
    },
    isActualVectorMerchOnly() {
      return this.actualVector && this.actualVector.attributes.some((el) => el.key == 'merch_only');
    },
    seeMoreLink() {
      if (this.isActualVectorMerchOnly) {
        return this.$urlBuilder('merch-plans', { keyword: 'preview', comparePlans: true });
      } else {
        return this.$urlBuilder('plans', { keyword: 'preview', comparePlans: true });
      }
    },
    isPurchasePage() {
      return this.$route.name.startsWith('purchase');
    },
    afterLoginPath() {
      return this.plansPage ? this.$urlBuilder('purchase') : '';
    },
    isPreviewPage() {
      return !this.plansPage && !this.isPurchasePage;
    },
    alternativeLayout() {
      return this.prismicProps.alternativeLayout;
    }
  },
  watch: {
    user: function (value) {
      if (value && !this.showPlans && this.purchaseFlowStatus != this.allStatus[3]) {
        this.changeStatus(this.allStatus[2]);
      }
    }
  },
  created() {
    this.setPlansToFetch();

    if (this.showPlans) {
      this.changeStatus(this.allStatus[0]);
    } else {
      if (!this.$auth.loggedIn) {
        this.changeStatus(this.allStatus[1]);
      } else {
        this.changeStatus(this.allStatus[2]);
      }
    }
  },
  mounted() {
    var src =
      (process.env.BLUESNAP_DOMAIN_PATH
        ? process.env.BLUESNAP_DOMAIN_PATH
        : 'https://sandbox.bluesnap.com') + '/web-sdk/4/bluesnap.js';

    if (!document.querySelector('script[src="' + src + '"]')) {
      var s = document.createElement('script');
      s.setAttribute('src', src);
      document.body.appendChild(s);

      s = document.createElement('script');
      s.setAttribute('src', 'https://js.stripe.com/v3/');
      document.body.appendChild(s);
    }

    if (!this.plansPage && !this.isPurchasePage && this.actualVector?.id) {
      this.cantDownloadReason = this.elementReason(this.actualVector.id);
    }
  },
  methods: {
    async login() {
      if (this.$auth.loggedIn) {
        if (!this.isPurchasePage && this.actualVector?.id) {
          await this.$store.dispatch('downloads/CHECK_DOWNLOAD_PERMISSION', this.actualVector.id);
        }

        if (!this.isPurchasePage && this.canDownload(this.actualVector?.id)) {
          const id = this.actualVector.id;
          const pathDownload = this.pathDownload({
            id,
            type: 'vector',
            slug: this.actualVector.canonizedName
          });
          const data = { id, url: this.actualVector.urlDownload };

          this.$ga.page(pathDownload);
          await this.$store.dispatch('downloads/DOWNLOAD_VECTOR', data);
          this.$store.commit('vectors/SET_SHOW_PURCHASE_FLOW', false);
          this.changeStatus();
          this.$bvModal.hide('purchase-modal');
        } else {
          this.changeStatus(this.allStatus[2]);
        }
      } else {
        this.changeStatus(this.allStatus[1]);
      }
    },
    payment(paymentMethod) {
      this.changeStatus(this.allStatus[3]);
      this.$ga.event({
        eventCategory: 'purchase',
        eventAction: `${this.openPlan.id}`,
        eventLabel: paymentMethod,
        eventValue: parseFloat(this.openPlan.cost)
      });
    },
    plansSubscribeClick() {
      if (this.plansPage) {
        this.$store.commit('vectors/SET_ACTUAL_VECTOR', { vector: null, vectorId: null });
      }
      if (this.plansNewPage) {
        this.$router.push(this.$urlBuilder('purchase'));
      } else {
        if (this.$auth.loggedIn) {
          this.changeStatus(this.allStatus[2]);
        } else {
          this.changeStatus(this.allStatus[1]);
        }
      }
    },
    setPlansToFetch() {
      let amount = 3;
      if (this.prismicProps?.primaryIds?.length) {
        amount = this.prismicProps?.primaryIds?.filter((elem) => {
          return elem ?? false;
        }).length;
      }
      this.$store.commit('plans/SET_PLANS_TO_FETCH', { uId: this.uId, amount });
    },
    freeDownload() {
      const downloadsCookies = this.$cookies.get('vx.downloads')
        ? this.$cookies.get('vx.downloads')
        : 0;
      if (!this.$auth.loggedIn && downloadsCookies >= 10) this.$bvModal.show('user-modal');
      else if (!this.$auth.loggedIn && downloadsCookies < 10)
        this.$cookies.set('vx.downloads', downloadsCookies + 1);
      this.$emit('start-free-download');
    },
    changeStatus(status = '') {
      this.$store.commit('vectors/SET_PURCHASE_FLOW_STATUS', status);
    }
  }
};
</script>

<style lang="scss" scoped>
.purchase-flow-container {
  border-radius: 5px;
  .see-more {
    color: #474747;
  }
}
.background {
  z-index: 2;
  position: relative;
}
</style>
